<template>
  <b-row class="position-relative" :class="[data.skip_mode === skipTypes.SKIP_ICON ? 'mt-5' : 'mt-4']">
    <b-close-button @click="$emit('skip')" v-if="data.skip_mode === skipTypes.SKIP_ICON"
                    :class="['close-button', bgIsLight ? 'dark' : 'light']" />

    <b-col cols="12" class="text-center mb-3" style="font-size: 12px;" v-if="data.show_powered_by">Powered by <span class="fw-bold">getpica.com</span></b-col>

    <b-col cols="12" class="mt-3 mb-4" v-if="data.logo">
      <b-img class="logo" :src="data.logo" center fluid/>
    </b-col>
    <b-col v-if="translatedText(data.title)" cols="12" class="mb-4 text-center mt-2">
      <h1 class="title">{{ translatedText(data.title) }}</h1>
    </b-col>
  </b-row>
  <b-form novalidate @submit.prevent="onSubmit">
    <b-row>
      <div :class="[getFieldClasses(field.size), 'mb-3']" v-for="field in data.fields" :key="field.key">
        <TextInput :data="field" :lang="lang" :is-submitted="isSubmitted" v-if="field.type === fieldTypes.TEXT" v-model="form[field.key]"/>
        <PhoneInput :data="field" :lang="lang" :is-submitted="isSubmitted" v-if="field.type === fieldTypes.PHONE" v-model="form[field.key]"/>
        <EmailInput :data="field" :lang="lang" :is-submitted="isSubmitted" v-if="field.type === fieldTypes.EMAIL" v-model="form[field.key]"/>
        <SelectInput :data="field" :lang="lang" :is-submitted="isSubmitted" v-if="field.type === fieldTypes.SELECT" v-model="form[field.key]"/>
        <MultyCheckboxes :data="field" :is-submitted="isSubmitted" :lang="lang" v-if="field.type === fieldTypes.MULTY_SELECT" v-model="form[field.key]"/>
        <DateInput :data="field" :lang="lang" :is-submitted="isSubmitted" v-if="field.type === fieldTypes.DATE" v-model="form[field.key]"/>
      </div>
    </b-row>
    <b-row class="consents">
      <b-col cols="12" v-for="consent in data.consents" :key="consent.key">
        <ConsentInput :data="consent" :lang="lang" :is-submitted="isSubmitted" v-model="form[consent.key]"/>
      </b-col>
    </b-row>
    <b-row class="justify-content-md-center form-footer mt-3">
      <b-col cols="12" :md="data.skip_mode === skipTypes.SKIP_BUTTON ? 3 : 12" class="mb-2">
        <b-button type="submit" class="d-block w-100" variant="pica-button" :disabled="loading">
          <b-spinner v-if="loading" small class="me-2"></b-spinner>
          {{ translatedText({ it: 'Invia', en: 'Send', es: 'Enviar', pt: 'Enviar', de: 'Senden', fr: 'Envoyer', sl: 'Pošlji', ru: 'Отправить', uk: 'Надіслати', pl: 'Wyślij' }) }}
        </b-button>
      </b-col>
      <b-col cols="12" md="3" class="mb-2" v-if="data.skip_mode === skipTypes.SKIP_BUTTON">
        <b-button variant="link" class="d-block w-100" @click="$emit('skip')">{{ translatedText({ it: 'Salta', en: 'Skip', es: 'Omitir', pt: 'Pular', de: 'Überspringen', fr: 'Ignorer', sl: 'Preskoči', ru: 'Пропустить', uk: 'Пропустити', pl: 'Pomiń' }) }}</b-button>
      </b-col>
    </b-row>
  </b-form>
</template>


<script setup>
import { ref } from "vue"
import {translate} from "@/domain/utils/translations";
import MultyCheckboxes from "@/domain/pform/input/MultyCheckboxes.vue";
import TextInput from "./input/TextInput.vue";
import SelectInput from "./input/SelectInput.vue";
import ConsentInput from "./input/ConsentInput.vue";
import {fieldTypes, skipTypes, fieldSizes} from "./fields";
import {computed, reactive, watch} from "vue";
import PhoneInput from "./input/PhoneInput.vue";
import EmailInput from "./input/EmailInput.vue";
import DateInput from "./input/DateInput.vue";
import {isLightColor} from "@/domain/utils/styles";
const emit = defineEmits(['send', 'skip'])
const props = defineProps({
  data: {
    type: Object,
    required: true
  },
  lang: {
    type: String,
    required: true
  },
  defaultValues: {
    type: Object,
    default: () => ({})
  },
  loading: {
    type: Boolean,
    default: false
  }
})


const data = computed(() => props.data) // We can't watch props directly, it will break the reference, so we need to use computed to bypass it

// Make form fields dynamic and reactive based on data
let form = reactive({})

const isSubmitted = ref(false)

const bgIsLight = computed(() => {
  if (data.value && data.value.style && data.value.style.bg_color) {
    return isLightColor(data.value.style.bg_color);
  }
  return true
});

// Track loaded fonts to avoid re-loading (must be declared before watch with immediate: true)
const loadedFonts = new Set()

watch([data, () => props.defaultValues], ([newForm, defaultVals]) => {
  if (newForm?.style) {
    applyStyles(newForm.style)
  }

  // Clear existing form data
  Object.keys(form).forEach(key => delete form[key])
  
  // Add new fields with default values
  newForm.fields.forEach(field => {
    const defaultValue = defaultVals && defaultVals[field.key] !== undefined 
      ? defaultVals[field.key] 
      : (field.type === fieldTypes.MULTY_SELECT ? [] : '')
    
    form[field.key] = defaultValue
  })
  
  newForm.consents.forEach(consent => {
    const defaultValue = defaultVals && defaultVals[consent.key] !== undefined 
      ? defaultVals[consent.key] 
      : false
    form[consent.key] = defaultValue
  })
}, {immediate: true, deep: true})


const onSubmit = () => {
  if (props.loading) return
  isSubmitted.value = true
  if (formIsValid.value) emit('send', form)
}

const formIsValid = computed(() => {
  const isValidForSubmit = (fieldType, mandatory, value) => {
    if (!mandatory && (!value || value === '' || (Array.isArray(value) && value.length === 0))) {
      return true;
    }
    
    switch (fieldType) {
      case 'email':
        return /^[^@]+@[^@]+\.[^@]+$/.test(value);
      case 'text':
        return value && value.length > 0;
      case 'phone':
        return /^\+?[0-9]+$/.test(value) && value.length >= 2;
      case 'select':
        return value && value !== '';
      case 'multySelect':
        return Array.isArray(value) && value.length > 0;
      case 'boolean':
        console.log(value)
        return value === true;
      case 'date':
        if (value.length <= 0) return false;
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(value)) return false;
        const date = new Date(value);
        return date instanceof Date && !isNaN(date) && value === date.toISOString().split('T')[0];
      default:
        return true;
    }
  };
  
  for (const field of data.value.fields)
    if (!isValidForSubmit(field.type, field.mandatory, form[field.key])) return false
  for (const consent of data.value.consents)
    if (!isValidForSubmit('boolean', consent.mandatory, form[consent.key])) return false
  return true
})

const translatedText = (field) => translate(field, props.lang)

const getFieldClasses = (size) => {
  switch (size) {
    case fieldSizes.FULL:
      return 'col-12'
    case fieldSizes.HALF:
      return 'col-12 col-sm-6'
    case fieldSizes.THIRD:
      return 'col-12 col-sm-4'
    default:
      return 'col-12 col-sm-6' // default to half
  }
}

function isValidColor(color) {
  if (!color || typeof color !== 'string') return false
  // Check hex colors (#xxx or #xxxxxx)
  if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color)) return true
  // Check rgb/rgba
  if (/^rgba?\(/.test(color)) return true
  return false
}

function applyStyles(style) {
  const root = document.querySelector(':root')
  root.style.setProperty('--bs-body-bg', style.bg_color)
  root.style.setProperty('--bs-body-color', isLightColor(style.bg_color) ? 'var(--bs-black)' : 'var(--bs-white)')
  root.style.setProperty('--bs-border-radius', `${style.input_radius}px`)
  root.style.setProperty('--bs-link-color', style.primary_color)
  root.style.setProperty('--bs-link-hover-color', style.primary_color)
  root.style.setProperty('--bs-primary', style.primary_color)
  root.style.setProperty('--bs-heading-color', style.title_color)

  // Button color with fallback to primary_color
  const buttonColor = isValidColor(style.button_color) ? style.button_color : style.primary_color
  root.style.setProperty('--button-bg-color', buttonColor)
  root.style.setProperty('--color-button-text', isLightColor(buttonColor) ? 'var(--bs-black)' : 'var(--bs-white)')

  // Load custom font if specified
  if (style.font) {
    loadFont(style.font, root)
  }

  // Apply custom CSS if specified
  applyCustomCss(style.custom_css)
}

function loadFont(fontConfig, root) {
  const setFontFamily = (fontFamily) => {
    // Set both our custom variable and Bootstrap's variable
    root.style.setProperty('--form-font-family', fontFamily)
    root.style.setProperty('--bs-body-font-family', fontFamily)
  }

  // Apply font scale
  const scale = fontConfig?.scale || 1
  if (scale !== 1) {
    document.documentElement.style.fontSize = `calc(16px * ${scale})`
  } else {
    document.documentElement.style.fontSize = ''
  }

  if (!fontConfig || fontConfig.type === 'default' || fontConfig.type === 'system') {
    return
  }

  if (fontConfig.type === 'google' && fontConfig.google_font) {
    const fontName = fontConfig.google_font
    if (!loadedFonts.has(fontName)) {
      const link = document.createElement('link')
      link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/ /g, '+')}:wght@400;700&display=swap`
      link.rel = 'stylesheet'
      document.head.appendChild(link)
      loadedFonts.add(fontName)
    }
    setFontFamily(`'${fontName}', sans-serif`)
  } else if (fontConfig.type === 'custom' && fontConfig.custom_font_url) {
    const fontName = fontConfig.custom_font_name || 'CustomFont'
    if (!loadedFonts.has(fontName)) {
      const font = new FontFace(fontName, `url(${fontConfig.custom_font_url})`)
      font.load().then(() => {
        document.fonts.add(font)
        loadedFonts.add(fontName)
      }).catch(err => console.error('Error loading custom font:', err))
    }
    setFontFamily(`'${fontName}', sans-serif`)
  }
}

function applyCustomCss(css) {
  let styleEl = document.getElementById('pica-custom-css')

  if (!css || css.trim() === '') {
    // Remove existing custom CSS if no CSS provided
    if (styleEl) {
      styleEl.remove()
    }
    return
  }

  if (!styleEl) {
    styleEl = document.createElement('style')
    styleEl.id = 'pica-custom-css'
    document.head.appendChild(styleEl)
  }

  styleEl.textContent = css
}

</script>